import { ApiError } from './api-error';
import type { AuthClient } from './auth-client';
import type { SessionStatus, SessionStore } from './session-store';

/** Resultado de uma tentativa de renovar a sessão. */
export type RefreshOutcome = Exclude<SessionStatus, 'unknown'>;

/** Renovação de sessão compartilhada (single-flight). */
export interface SessionRefresher {
  refresh: () => Promise<RefreshOutcome>;
}

const ORIGIN_NOT_ALLOWED = 'AUTH_ORIGIN_NOT_ALLOWED';
/** Lock compartilhado entre abas: só uma aba renova o refresh token por vez. */
const REFRESH_LOCK = 'jaf-auth-refresh';

function outcomeOf(error: unknown): Exclude<RefreshOutcome, 'authenticated'> {
  if (!(error instanceof ApiError)) return 'unavailable';
  if (error.status === 401) return 'anonymous';
  if (error.status === 403 && error.code === ORIGIN_NOT_ALLOWED) return 'origin-not-allowed';
  if (error.status === 403) return 'forbidden';
  return 'unavailable';
}

function currentOutcome(store: SessionStore): RefreshOutcome {
  const { status } = store.getState();
  return status === 'unknown' ? 'anonymous' : status;
}

/**
 * Executa com o lock entre abas (Web Locks). O refresh token é rotativo: duas abas renovando
 * ao mesmo tempo com o mesmo cookie fariam a segunda ser recusada. Com o lock, a segunda aba
 * espera e já envia o cookie novo. Sem suporte a Web Locks, executa direto.
 */
function withCrossTabLock<T>(work: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  return locks === undefined ? work() : locks.request(REFRESH_LOCK, work);
}

/**
 * Cria o refresher: chamadas concorrentes compartilham a mesma promise. Uma resposta que chega
 * depois de logout/troca de sessão é descartada e não restaura a sessão anterior.
 */
export function createSessionRefresher(auth: AuthClient, store: SessionStore): SessionRefresher {
  let inFlight: Promise<RefreshOutcome> | null = null;

  async function run(): Promise<RefreshOutcome> {
    const generation = store.getState().generation;
    try {
      const response = await withCrossTabLock(() => auth.refresh());
      if (store.getState().generation !== generation) return currentOutcome(store);
      store.getState().authenticate(response);
      return 'authenticated';
    } catch (error) {
      const outcome = outcomeOf(error);
      if (store.getState().generation === generation) store.getState().end(outcome);
      return outcome;
    }
  }

  return {
    refresh: () => {
      inFlight ??= run().finally(() => {
        inFlight = null;
      });
      return inFlight;
    },
  };
}
